import { safeSetDoc } from '../utils';
import { refreshRecommendations } from '../hooks/useRecommendations';
import { useState, useEffect, useRef, memo, useMemo } from 'react';
import { usePersistentState } from '../hooks/usePersistentState';
import { debounce } from '../utils/debounce';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { getMarkerGradient } from '../utils';
import { Play, Pause, CloudRain, X, Navigation, Utensils, Bed, Plane, Music, MapPin as MapPinIcon, Calendar, BookmarkPlus, Edit3, Image as ImageIcon, HelpCircle, Martini, Coffee, Binoculars, ShoppingBag, Ticket, Tag } from 'lucide-react';
import { getItemColor } from '../utils';
import { renderToString } from 'react-dom/server';
import MapSearchBox from './MapSearchBox';
import { checkPlacesRateLimit } from '../utils';

// Fix for default marker icons in Leaflet + React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

let globalMapState = { lat: null, lng: null, zoom: null };
let globalLastProps = { lat: null, lng: null };

const MapController = ({ lat, lng, recenterTrigger }) => {
  const map = useMap();

  useEffect(() => {
    const curLat = parseFloat(lat);
    const curLng = parseFloat(lng);
    
    // Auto-recenter ONLY if coordinates prop changed from last time
    if (!isNaN(curLat) && !isNaN(curLng)) {
      if (curLat !== globalLastProps.lat || curLng !== globalLastProps.lng) {
        if (import.meta.env.DEV) console.log('[MapController] Auto-centering to new props:', { curLat, curLng });
        map.setView([curLat, curLng], 12);
        globalLastProps = { lat: curLat, lng: curLng };
      } else if (globalMapState.lat && globalMapState.lng) {
        // Props didn't change, but component remounted. Restore user's last pan/zoom state.
        map.setView([globalMapState.lat, globalMapState.lng], globalMapState.zoom || 12, { animate: false });
      }
    }

    const onMoveEnd = () => {
      const center = map.getCenter();
      globalMapState.lat = center.lat;
      globalMapState.lng = center.lng;
      globalMapState.zoom = map.getZoom();
    };

    map.on('moveend', onMoveEnd);
    return () => { map.off('moveend', onMoveEnd); };
  }, [lat, lng, map]);

  // Handle explicit recenter click
  useEffect(() => {
    if (recenterTrigger > 0) {
      const curLat = parseFloat(lat);
      const curLng = parseFloat(lng);
      if (!isNaN(curLat) && !isNaN(curLng)) {
        if (import.meta.env.DEV) console.log('[MapController] Explicit recenter to:', { curLat, curLng });
        map.flyTo([curLat, curLng], 12, { duration: 1.5 });
      }
    }
  }, [recenterTrigger, map, lat, lng]);

  return null;
};

const MapSearchController = ({ searchLocation }) => {
  const map = useMap();
  useEffect(() => {
    if (searchLocation && searchLocation.lat && searchLocation.lng) {
      map.flyTo([searchLocation.lat, searchLocation.lng], 16, { duration: 1.5 });
    }
  }, [searchLocation, map]);
  return null;
};

const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(map.getContainer());
    return () => resizeObserver.disconnect();
  }, [map]);
  return null;
};

const getMarkerIcon = (events, lat, lng, isActive = false) => {
  let isTentative = false;

  let hasNormalEvent = false;
  let hasRestaurant = false;
  let hasDrinks = false;
  let hasCoffee = false;
  let hasSight = false;
  let hasConcert = false;
  let hasMusicVenue = false;
  let isWantToGo = false;
  let hasHotel = false;
  
  const typeFallback = events[0]?.type;
  let multiTypes = events[0]?.multiTypes || [];

  events.forEach(e => {
    if (e.type === 'tentative') isTentative = true;
    else if (e.type === 'event') hasNormalEvent = true;
    else if (e.type === 'restaurant' || (e.isRecommendation && e.type === undefined)) {
       hasRestaurant = true;
       if (e.data?.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go')) isWantToGo = true;
    }
    else if (e.type === 'drinks') {
       hasDrinks = true;
       if (e.data?.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go')) isWantToGo = true;
    }
    else if (e.type === 'wanttogo') {
       isWantToGo = true;
    }
    else if (e.type === 'coffee') {
       hasCoffee = true;
       if (e.data?.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go')) isWantToGo = true;
    }
    else if (e.type === 'sight') {
       hasSight = true;
       if (e.data?.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go')) isWantToGo = true;
    }
    else if (e.type === 'concert') {
       hasConcert = true;
    }
    else if (e.type === 'musicvenue') {
       hasMusicVenue = true;
       if (e.data?.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go')) isWantToGo = true;
    }
    else if (e.type === 'hotel') hasHotel = true;
    
    if (e.isWantToGo || (e.tags && Array.from(e.tags).some(t => typeof t === 'string' && t.toLowerCase() === 'want to go'))) {
      isWantToGo = true;
    }
  });

  let IconComponent = Calendar;
  let baseColor = getItemColor({ type: typeFallback });

  if (hasRestaurant) {
    IconComponent = Utensils;
    isTentative = false;
  } else if (hasDrinks) {
    IconComponent = Martini;
    isTentative = false;
  } else if (hasCoffee) {
    IconComponent = Coffee;
    isTentative = false;
  } else if (hasSight) {
    IconComponent = Binoculars;
    isTentative = false;
  } else if (hasConcert) {
    IconComponent = Ticket;
    isTentative = false;
  } else if (hasMusicVenue) {
    IconComponent = Music;
    isTentative = false;
  } else if (hasHotel) {
    IconComponent = Bed;
    isTentative = false;
  } else if (hasNormalEvent) {
    IconComponent = Calendar;
    isTentative = false; // Mixed uses regular icon
  } else if (isTentative) {
    IconComponent = HelpCircle;
  } else {
    if (typeFallback === 'flight') { IconComponent = Plane; }
    else if (typeFallback === 'concert') { IconComponent = Ticket; }
    else if (typeFallback === 'musicvenue') { IconComponent = Music; }
    else if (typeFallback === 'sight') { IconComponent = Binoculars; }
    else if (typeFallback === 'photo') { IconComponent = ImageIcon; }
    else if (typeFallback === 'wanttogo') { IconComponent = BookmarkPlus; }
    else if (typeFallback === 'shopping') { IconComponent = ShoppingBag; }
    else if (typeFallback === 'alias') { IconComponent = Tag; }
    else { IconComponent = Calendar; }
    isTentative = false;
  }

  let iconHtml = renderToString(<IconComponent size={14} color="white" strokeWidth={3} />);

  const borderStyle = isWantToGo ? 'border: 3px solid #10b981;' : (isTentative ? 'border: 2px dashed rgba(255,255,255,0.8);' : 'border: 2px solid white;');
  const opacity = isTentative ? 0.85 : 1;

  let bgStyle = `background-color: ${baseColor};`;
  if (multiTypes.length > 1) {
    iconHtml = ''; // Remove icon for multi-colored pins
    bgStyle = `background: ${getMarkerGradient(multiTypes)};`;
  }

  return L.divIcon({
    className: 'custom-marker bg-transparent border-none',
    html: `
      <div 
        style="
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: -6px;
          cursor: pointer;
          pointer-events: auto;
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
          ${isActive ? 'transform: scale(1.3); z-index: 1000;' : ''}
        "
      >
        <div style="
          ${bgStyle}
          width: 26px; 
          height: 26px; 
          ${borderStyle} 
          border-radius: 50% 50% 50% 0; 
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: ${opacity};
          box-shadow: ${isActive ? '0 8px 16px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.2)'};
          transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        ">
          <div style="transform: rotate(45deg); display: flex;">
            ${iconHtml}
          </div>
        </div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
};

// Global cache for RainViewer metadata to prevent 429 rate limits
let cachedRadarData = null;
let cachedRadarFetchTime = 0;

const InteractiveMap = memo(({ lat, lng, locationName = "Current Location", markers = [], showRadar = false, recommendations = [], aliases = [], authUser, dispatch }) => {
  if (markers.length > 0) {
    // console.log('[InteractiveMap] Sample marker:', markers[0]);
  }

  const [radarData, setRadarData] = useState(null);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [localRadarEnabled, setLocalRadarEnabled] = usePersistentState('nytm.map.filters.localRadarEnabled', false);
  const [mapStyle, setMapStyle] = usePersistentState('nytm.map.filters.mapStyle', 'plain'); // 'osm' or 'plain'
  const [showAliases, setShowAliases] = usePersistentState('nytm.map.filters.showAliases', false);
  const [searchLocation, setSearchLocation] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [externalPlaceId, setExternalPlaceId] = useState(null);

  useEffect(() => {
    const handleOpenPlaceDetails = (e) => {
      if (e.detail && e.detail.placeId) {
        setExternalPlaceId(e.detail.placeId);
      }
    };
    window.addEventListener('OPEN_PLACE_DETAILS', handleOpenPlaceDetails);
    return () => window.removeEventListener('OPEN_PLACE_DETAILS', handleOpenPlaceDetails);
  }, []);

  const MapClickHandler = () => {
    useMapEvents({
      click() {
        setActiveGroup(null);
        setSearchLocation(null);
      }
    });
    return null;
  };

  const aggregatedMarkers = useMemo(() => {
    const groups = {};
    
    // Build active markers list (swap aliases if toggle is on)
    let activeMarkers = [];
    if (showAliases) {
      activeMarkers = markers.filter(m => m.category !== 'Recommendation' && m.category !== 'Friend Recommendation' && m.category !== 'My Recommendation');
      // Add aliases
      aliases.forEach(a => {
        activeMarkers.push({
          id: `alias-${a.id}`,
          lat: a.lat,
          lng: a.lng,
          title: a.alias || 'Alias',
          venueName: a.alias || 'Alias',
          address: a.address || '',
          type: 'alias',
          category: 'Alias',
          isAlias: true,
          placeId: a.placeId
        });
      });
    } else {
      activeMarkers = markers;
    }

    (activeMarkers || []).forEach((m) => {
      const key = `${m.lat},${m.lng}`;
      if (!groups[key]) {
        groups[key] = { lat: m.lat, lng: m.lng, events: [], seenTitles: new Set(), seenVenues: new Set() };
      }
      
      // Deduplicate O(1): check sets instead of looping through all events
      const cleanTitle = (t) => t ? t.replace(/\s*\([^)]*\)$/, '').toLowerCase().trim() : '';
      const mTitle = cleanTitle(m.title);
      const mVenue = m.venueName ? m.venueName.toLowerCase().trim() : '';
      
      let isDuplicate = false;
      if (mTitle && groups[key].seenTitles.has(mTitle)) isDuplicate = true;
      if (mVenue && groups[key].seenVenues.has(mVenue)) isDuplicate = true;
      
      if (!isDuplicate) {
        if (mTitle) groups[key].seenTitles.add(mTitle);
        if (mVenue) groups[key].seenVenues.add(mVenue);
        groups[key].events.push(m);
      }
    });

    return Object.values(groups).map((group) => {
      // Find a representative name for the group: Venue -> Address -> City
      let locName = '';
      for (const e of group.events) {
        if (e.venueName) { locName = e.venueName; break; }
        if (e.address && !locName) { locName = e.address; }
        if (e.city && !locName) { locName = e.city; }
      }
      group.locationName = locName || 'Unknown Location';

      // Sort events in group chronologically: [startDate, startTime]
      group.events.sort((a, b) => {
        const dateA = a.startDate || '';
        const dateB = b.startDate || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);

        if (a.isAllDay && !b.isAllDay) return -1;
        if (!a.isAllDay && b.isAllDay) return 1;

        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      return group;
    });
  }, [markers, showAliases, aliases]);

  useEffect(() => {
    const handleMarkerClick = (e) => {
      if (!e.detail) return;
      const { lat, lng } = e.detail;
      // Search Location Pin
      if (searchLocation && Math.abs(searchLocation.lat - lat) < 0.0001 && Math.abs(searchLocation.lng - lng) < 0.0001) {
        setActiveGroup({
          lat: searchLocation.lat,
          lng: searchLocation.lng,
          locationName: searchLocation.address,
          events: [{ 
            type: 'tentative', 
            title: searchLocation.address, 
            lat: searchLocation.lat, 
            lng: searchLocation.lng 
          }]
        });
        return;
      }
      // Aggregated Markers
      const group = aggregatedMarkers.find(g => Math.abs(g.lat - lat) < 0.0001 && Math.abs(g.lng - lng) < 0.0001);
      if (group) {
        setActiveGroup(group);
        
        // Refresh stale place data in the background (older than 7 days)
        const refreshStalePlaces = async (events) => {
          try {
            const placesLib = await window.google.maps.importLibrary("places");
            const { db } = await import('../firebase');
            const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
            const { appId: APP_ID_CONSTANT } = await import('../constants');
      
            let didUpdate = false;
            for (const event of events) {
              // Raw recommendations have placeId and userId directly on them
              // We check for placeName to ensure we don't accidentally fetch fields for calendar events or tasks
              if (!event.placeId || !event.userId || !event.placeName) continue;
              
              const nowMs = Date.now();
              const updatedAt = event.updatedAt?.seconds * 1000 || event.createdAt?.seconds * 1000 || 0;
              const summaryUpdatedAt = event.summaryUpdatedAt?.seconds * 1000 || updatedAt;
              
              const isBasicStale = nowMs - updatedAt > 1 * 24 * 60 * 60 * 1000; // 1 day
              const isSummaryStale = nowMs - summaryUpdatedAt > 30 * 24 * 60 * 60 * 1000; // 30 days

              if (isBasicStale || isSummaryStale) {
                const place = new placesLib.Place({ id: event.placeId });
                
                const fieldsToFetch = ['regularOpeningHours', 'websiteURI'];
                if (isSummaryStale) {
                  fieldsToFetch.push('editorialSummary');
                }

                if (!checkPlacesRateLimit()) {
                  console.warn('[InteractiveMap] Rate limit hit. Aborting background refresh.');
                  continue; // skip the fetch for this place
                }

                await place.fetchFields({ fields: fieldsToFetch });
                
                const updates = {
                  updatedAt: serverTimestamp()
                };
                
                if (isSummaryStale) {
                  updates.summaryUpdatedAt = serverTimestamp();
                }

                const placesUpdates = { updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid || 'unknown' };
                if (place.regularOpeningHours) {
                  updates.weekdayDescriptions = place.regularOpeningHours.weekdayDescriptions || [];
                  placesUpdates.hours = place.regularOpeningHours.weekdayDescriptions || [];
                }
                if (place.editorialSummary) {
                   updates.summary = place.editorialSummary;
                }
                if (place.websiteURI) {
                   updates.website = place.websiteURI;
                   placesUpdates.website = place.websiteURI;
                }
      
                const docId = `${event.userId}_${event.placeId}`;
                await safeSetDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'recommendations', docId), updates, { merge: true });
                await safeSetDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'places', event.placeId), placesUpdates, { merge: true });
                didUpdate = true;
              }
            }
            if (didUpdate) {
              await refreshRecommendations();
            }
          } catch (e) {
            console.error("Failed to refresh stale places:", e);
          }
        };
        
        const debounceRefreshStalePlaces = debounce(refreshStalePlaces, 300);
        debounceRefreshStalePlaces(group.events);
      }
    };
    
    window.addEventListener('MAP_MARKER_CLICK', handleMarkerClick);
    return () => {
      window.removeEventListener('MAP_MARKER_CLICK', handleMarkerClick);
    };
  }, [aggregatedMarkers, searchLocation]);

  useEffect(() => {
    // Intentionally ignoring showRadar prop to keep radar off by default
    // setLocalRadarEnabled(showRadar);
  }, []);
  const timerRef = useRef(null);

  // Initial center based on current props - strict fallback to SF, NOT Dallas
  const initialLat = parseFloat(lat) || 37.7749;
  const initialLng = parseFloat(lng) || -122.4194;

  // Fetch Radar Data from RainViewer
  useEffect(() => {
    let refreshInterval;
    const fetchRadarMetadata = async () => {
      // Use cached metadata if it's less than 5 minutes old
      if (cachedRadarData && Date.now() - cachedRadarFetchTime < 5 * 60 * 1000) {
        setRadarData(cachedRadarData);
        if (cachedRadarData.radar && cachedRadarData.radar.past) {
          setCurrentFrameIndex(cachedRadarData.radar.past.length - 1);
        }
        return;
      }

      try {
        const response = await fetch('https://api.rainviewer.com/public/weather-maps.json');
        const data = await response.json();
        cachedRadarData = data;
        cachedRadarFetchTime = Date.now();
        setRadarData(data);
        if (data.radar && data.radar.past) {
          setCurrentFrameIndex(data.radar.past.length - 1);
        }
      } catch (error) {
        console.error("Error fetching radar metadata:", error);
      }
    };
    if (localRadarEnabled) {
      fetchRadarMetadata();
      // Auto-refresh every 5 minutes (ignoring cache if needed, but we check cache inside)
      // Actually we should force fetch if we are auto-refreshing, but cache logic handles it because cache expires after 5 mins.
      refreshInterval = setInterval(() => {
        cachedRadarFetchTime = 0; // force bypass cache on auto-refresh
        fetchRadarMetadata();
      }, 5 * 60 * 1000);
    }
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [localRadarEnabled]);

  const allFrames = useMemo(() => {
    if (!radarData?.radar) return [];
    // Show past, plus exactly 30 minutes (3 frames) into the future
    return [...(radarData.radar.past || []), ...(radarData.radar.nowcast ? radarData.radar.nowcast.slice(0, 3) : [])];
  }, [radarData]);

  // Playback Logic
  useEffect(() => {
    if (isPlaying && localRadarEnabled && allFrames.length > 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCurrentFrameIndex((prevIndex) => 
          (prevIndex + 1) % allFrames.length
        );
      }, 2000); // Slower playback to prevent rate limiting 
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, localRadarEnabled, allFrames]);

  const currentTimestamp = allFrames[currentFrameIndex]?.time || 'static';



  const formatTime = (time) => {
    if (!time) return '';
    try {
      const [h, m] = time.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m}${ampm.toLowerCase()}`;
    } catch (e) {
      return time;
    }
  };

  return (
    <div className="relative w-full h-full group bg-zinc-100" data-testid="interactive-map">
      <style>{`
        .base-map-tiles {
          transition: filter 0.5s ease, opacity 0.5s ease !important;
          opacity: 1 !important;
          filter: ${localRadarEnabled ? 'grayscale(30%)' : 'none'};
        }
      `}</style>
      <MapContainer
        center={[initialLat, initialLng]}
        zoom={12}
        scrollWheelZoom={true}
        className="w-full h-full z-0"
        zoomControl={false}
      >
        <MapResizer />
        <TileLayer
          attribution={mapStyle === 'osm' ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'}
          url={mapStyle === 'osm' ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"}
          className="base-map-tiles"
        />

        {localRadarEnabled && allFrames.map((frame, index) => {
          const isCurrent = index === currentFrameIndex;
          const isNext = isPlaying && index === (currentFrameIndex + 1) % allFrames.length;
          
          if (!isCurrent && !isNext) return null;
          // Use color scheme 2 (Universal Blue) instead of 4 (Weather Channel) to avoid dark mode look
          const url = `${radarData.host}${frame.path}/256/{z}/{x}/{y}/2/1_1.png`;
          return (
            <TileLayer
              key={`radar-frame-${frame.time}`}
              url={url}
              opacity={isCurrent ? 0.65 : 0}
              zIndex={100}
              maxNativeZoom={7}
              tileSize={256}
              updateWhenIdle={true}
              updateWhenZooming={false}
              keepBuffer={2}
            />
          );
        })}

        <MarkerClusterGroup chunkedLoading={true} maxClusterRadius={40} showCoverageOnHover={false}>
          {aggregatedMarkers.map((group, idx) => {
            const isActive = activeGroup && activeGroup.lat === group.lat && activeGroup.lng === group.lng;
            return (
              <Marker 
                key={`${group.lat}-${group.lng}-${idx}`} 
                position={[parseFloat(group.lat), parseFloat(group.lng)]}
                icon={getMarkerIcon(group.events, parseFloat(group.lat), parseFloat(group.lng), isActive)}
                zIndexOffset={isActive ? 1000 : 0}
                eventHandlers={{
                  click: () => setActiveGroup(group)
                }}
              />
            );
          })}
        </MarkerClusterGroup>

        {searchLocation && (
          <Marker
            position={[searchLocation.lat, searchLocation.lng]}
            icon={L.divIcon({
              className: 'custom-marker bg-transparent border-none',
              html: `
                <div 
                  style="
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-top: -6px;
                    cursor: pointer;
                    pointer-events: auto;
                  "
                >
                  <div style="
                    background-color: #ef4444; 
                    width: 26px; 
                    height: 26px; 
                    border: 2px solid white; 
                    border-radius: 50% 50% 50% 0; 
                    transform: rotate(-45deg);
                    box-shadow: 2px 2px 6px rgba(0,0,0,0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    animation: pulse 2s infinite;
                  ">
                    <div style="transform: rotate(45deg); display: flex;">
                      ${renderToString(<MapPinIcon size={14} color="white" strokeWidth={3} />)}
                    </div>
                  </div>
                </div>
              `,
              iconSize: [40, 40],
              iconAnchor: [20, 40],
            })}
          >
            <Popup className="border-none rounded-xl shadow-xl overflow-hidden p-0 m-0">
              <div className="flex flex-col min-w-[150px] max-w-[250px] font-sans">
                <div className="bg-zinc-100 px-3 py-2 border-b border-zinc-200">
                  <h3 className="font-black text-xs text-zinc-900 m-0 p-0 leading-tight break-words">
                    {searchLocation.address}
                  </h3>
                </div>
                <div className="p-3 text-[11px] font-medium text-zinc-600">
                  Custom Location Search
                </div>
              </div>
            </Popup>
          </Marker>
        )}

        <MapController lat={lat} lng={lng} recenterTrigger={recenterTrigger} />
        <MapSearchController searchLocation={searchLocation} />
        <MapClickHandler />
      </MapContainer>
      
      <MapSearchBox 
        onPlaceSelected={(loc) => {
          setSearchLocation(loc);
          setActiveGroup(null);
        }} 
        onClearSearch={() => {
          setSearchLocation(null);
          setExternalPlaceId(null);
        }} 
        recommendations={recommendations}
        aliases={aliases}
        authUser={authUser}
        externalPlaceId={externalPlaceId}
      />

      <div className="absolute top-4 right-4 z-[500] flex gap-2">
        <button 
          onClick={(e) => {
             e.stopPropagation();
             setShowAliases(prev => !prev);
          }}
          className={`bg-zinc-900 px-3 py-2 rounded-xl border shadow-xl flex items-center justify-center transition-all ${showAliases ? 'text-white border-blue-500/50 bg-black' : 'text-zinc-500 border-white/10 hover:text-white hover:bg-black'}`}
          title="Toggle Private Aliases"
        >
          <Tag size={14} strokeWidth={2.5} />
        </button>

        <button 
          onClick={(e) => {
             e.stopPropagation();
             setMapStyle(prev => prev === 'osm' ? 'plain' : 'osm');
          }}
          className={`bg-zinc-900 px-3 py-2 rounded-xl border shadow-xl flex items-center justify-center transition-all ${mapStyle === 'plain' ? 'text-white border-blue-500/50 bg-black' : 'text-zinc-500 border-white/10 hover:text-white hover:bg-black'}`}
          title="Toggle Plain Map Style"
        >
          <MapPinIcon size={14} strokeWidth={2.5} />
        </button>

        {localRadarEnabled && (
          <button 
            data-testid="radar-play-pause"
            onClick={(e) => {
               e.stopPropagation();
               setIsPlaying(prev => !prev);
            }}
            className={`bg-zinc-900 px-3 py-2 rounded-xl border shadow-xl flex items-center justify-center transition-all text-white border-white/20 hover:bg-black`}
            title={isPlaying ? "Pause Radar" : "Play Radar"}
          >
            {isPlaying ? <Pause size={14} strokeWidth={2.5} /> : <Play size={14} strokeWidth={2.5} />}
          </button>
        )}

        <button 
          data-testid="radar-toggle"
          onClick={(e) => {
             e.stopPropagation();
             setLocalRadarEnabled(prev => !prev);
          }}
          className={`bg-zinc-900 px-3 py-2 rounded-xl border shadow-xl flex items-center justify-center transition-all ${localRadarEnabled ? 'text-white border-blue-500/50 bg-black scale-105' : 'text-zinc-500 border-white/10 hover:text-white hover:bg-black'}`}
          title="Toggle Storm Radar"
        >
            <CloudRain size={14} strokeWidth={2.5} />
        </button>

        <button 
          onClick={() => setRecenterTrigger(prev => prev + 1)}
          className="bg-zinc-900 text-white/90 px-3 py-2 rounded-xl border border-white/10 shadow-xl flex items-center gap-2 hover:bg-black hover:scale-105 active:scale-95 transition-all group/bubble"
          title="Recenter Map"
        >
            <Navigation size={12} className="text-blue-400 group-hover/bubble:animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest">{locationName}</span>
        </button>
      </div>

      {localRadarEnabled && isPlaying && allFrames.length > 0 && (
        <div className="absolute top-[60px] right-4 z-[500] bg-zinc-900/90 backdrop-blur-md rounded-xl shadow-2xl border border-white/10 px-3 py-2 flex flex-col items-center gap-1.5 w-48 pointer-events-none transition-all">
          <div className="w-full flex justify-between items-center text-[10px] font-black text-white uppercase tracking-wider">
            <span className="text-zinc-400">
              {currentFrameIndex < (radarData?.radar?.past?.length || 0) ? "Past 2h" : "Forecast"}
            </span>
            <span className="text-blue-400">
              {new Date(allFrames[currentFrameIndex].time * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>
          <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-300 ease-linear ${currentFrameIndex < (radarData?.radar?.past?.length || 0) ? 'bg-blue-500' : 'bg-fuchsia-500'}`}
              style={{ width: `${(currentFrameIndex / (allFrames.length - 1)) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="absolute bottom-2 right-2 z-[500] opacity-30 hover:opacity-100 transition-opacity">
        <span className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter">RainViewer API &bull; OpenStreetMap</span>
      </div>

      {activeGroup && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-11/12 max-w-sm bg-white rounded-2xl shadow-2xl z-[1000] flex flex-col overflow-hidden border border-zinc-200 animate-in slide-in-from-bottom-10 fade-in duration-300">
          {/* Details Scroll Area */}
          <div className="p-4 flex flex-col gap-4 max-h-[40vh] overflow-y-auto custom-scrollbar relative">
            <div className="absolute top-2 right-2">
              <button 
                onClick={(e) => { e.stopPropagation(); setActiveGroup(null); }} 
                className="p-1.5 bg-zinc-100 text-zinc-500 rounded-full hover:bg-zinc-200 hover:text-zinc-800 transition-all"
              >
                <X size={16} strokeWidth={3} />
              </button>
            </div>
            {activeGroup.events.map((event, idx) => (
              <div key={idx} className="flex flex-col gap-2 pb-4 border-b border-zinc-100 last:border-0 last:pb-0">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex flex-col flex-1 min-w-0">
                    <h2 
                      className="text-sm font-black text-zinc-900 tracking-tight leading-tight uppercase truncate cursor-pointer hover:underline flex items-baseline gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        if ((event.isRecommendation || event.type === 'alias') && event.placeId) {
                          const evt = new CustomEvent('OPEN_PLACE_DETAILS', { detail: { placeId: event.placeId } });
                          window.dispatchEvent(evt);
                        } else {
                          const evt = new CustomEvent('OPEN_EDIT_EVENT_FROM_MAP', { detail: event });
                          window.dispatchEvent(evt);
                        }
                      }}
                    >
                      <span className="truncate">{event.title || event.venueName || activeGroup.locationName}</span>
                      {event.distanceLabel && (
                        <span className="text-[10px] font-bold text-zinc-400 shrink-0 tracking-widest">{event.distanceLabel}</span>
                      )}
                      {event.type === 'tentative' ? '?' : ''}
                    </h2>
                    {(event.address || activeGroup.locationName) && (event.address !== event.title) && (
                      <p className="text-[10px] text-zinc-500 font-medium truncate mt-0.5">
                        {event.address || activeGroup.locationName}
                      </p>
                    )}
                  </div>
                  {event.startDate && (
                    <div className="flex flex-col items-end shrink-0">
                      <span className="text-[10px] font-black text-zinc-900 uppercase">
                        {new Date(event.startDate + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </span>
                      {event.startTime && (
                        <span className="text-[8px] font-bold text-zinc-500 uppercase mt-0.5">
                          {formatTime(event.startTime)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {(event.description || event.note) && (
                  <p className="text-xs text-zinc-600 font-medium leading-snug line-clamp-3">
                    {event.description || event.note}
                  </p>
                )}
                
                {event.type === 'photo' && event.url && (
                  <div className="mt-2 rounded-xl overflow-hidden shadow-sm border border-zinc-100">
                    <img src={event.url} alt={event.title} className="w-full h-auto object-cover max-h-48" />
                  </div>
                )}

                {event.tags && event.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {event.tags.map(tag => (
                      <span key={tag} className="bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {event.recommendedBy && event.recommendedBy.length > 0 && (
                  <div className="text-[9px] text-zinc-400 font-bold uppercase mt-1">
                    Recommended by {event.recommendedBy.length} {event.recommendedBy.length === 1 ? 'person' : 'people'}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-2 pt-2 border-t border-zinc-50 border-dashed">
                  {['restaurant', 'drinks', 'coffee', 'sight', 'concert'].includes(event.type) ? (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(new CustomEvent('ADD_TENTATIVE_FROM_MAP', { detail: {
                          title: event.title || event.venueName || activeGroup.locationName,
                          venueName: event.venueName || activeGroup.locationName,
                          address: event.address || '',
                          city: event.city || '',
                          placeId: event.placeId,
                          lat: event.lat || activeGroup.lat,
                          lng: event.lng || activeGroup.lng,
                          startDate: event.startDate,
                          endDate: event.endDate
                        }}));
                        setActiveGroup(null);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-blue-700 transition-colors shadow-md shadow-blue-500/20"
                    >
                      <BookmarkPlus size={12} strokeWidth={3} />
                      Add to Itinerary
                    </button>
                  ) : null}
                  
                  {['event', 'concert', 'flight', 'hotel', 'tentative'].includes(event.type) && !event.isReadOnly ? (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(new CustomEvent('OPEN_EDIT_EVENT_FROM_MAP', { detail: event }));
                        setActiveGroup(null);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-zinc-900 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-black transition-colors shadow-md shadow-zinc-900/20"
                    >
                      <Edit3 size={12} strokeWidth={3} />
                      Edit Event
                    </button>
                  ) : null}

                  {activeGroup.lat && activeGroup.lng && (
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${activeGroup.lat},${activeGroup.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-3 py-2 bg-zinc-100 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200 rounded-lg transition-colors border border-zinc-200"
                      title="Get Directions"
                    >
                      <Navigation size={12} strokeWidth={2.5} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

InteractiveMap.displayName = 'InteractiveMap';

export default InteractiveMap;
